import {
    LotOptionRow,
    createdAtLine,
    createdAtText,
    scentingDateText,
} from './lot-option-row'
import type {
    SortingLot,
    SortingLotStockInNoteItem,
} from '@/lib/api/sorting-lot-api'
import type {
    ScentingLot,
    ScentingLotStockInNoteItem,
} from '@/lib/api/scenting-lot-api'
import type {
    PackagingLot,
    PackagingLotStockInNoteItem,
} from '@/lib/api/packaging-lot-api'
import type { AgriMaterialLot } from '@/lib/api/agri-material-lot-api'
import type { LotOption } from './lot-items-field'
import { agriUnitLabel } from '@/components/agricultural-materials/agri-unit-label'
import { formatQty } from '@/lib/format'
import { m } from '@/lib/paraglide/messages'

// ---- Vật tư nông nghiệp: dòng 1 = tên vật tư. Lô ảo 'STOCK' (qty-mode) hiện
// "Tồn kho" thay vì mã STOCK. ----
export function agriLotOption(l: AgriMaterialLot): LotOption {
    // Đơn vị agri lưu enum tiếng Anh (box/bag/...) → map sang nhãn tiếng Việt.
    const unit = agriUnitLabel(l.material_unit)
    const isVirtual = l.code === 'STOCK'
    const codeLabel = isVirtual ? m.aml_lot_virtual_stock() : l.code
    return {
        value: l.id,
        label: `${isVirtual ? '' : `${l.code} — `}${l.material_name ?? ''}`,
        available: l.quantity_available,
        unit,
        render: () => (
            <LotOptionRow
                title={l.material_name || codeLabel}
                code={codeLabel}
                qty={`${formatQty(l.quantity_available)}${unit ? ` ${unit}` : ''}`}
                // Lô ảo STOCK (tồn kho gộp) không có ngày tạo thật → bỏ dòng "Tạo:".
                createdLine={
                    isVirtual ? undefined : createdAtLine(l.created_at)
                }
            />
        ),
    }
}

// Bố cục 2 dòng cho từng loại lô (xem lot-option-row): dòng 1 = mô tả "là gì",
// dòng 2 = mã lô + chip ngắn (trái) · KL khả dụng (phải).

// ---- Phân loại: dòng 1 = loại chè ----
export function sortingLotOption(l: SortingLot, ledger = false): LotOption {
    return {
        value: l.id,
        // `label` GIỮ mã lô kể cả ở kiểu sổ: nó là từ khoá gõ để tìm. Chữ
        // hiện trên nút sau khi chọn thì lấy `triggerLabel` — không có mã.
        label: `${l.code} — ${l.grade_name ?? ''}`,
        triggerLabel: ledger
            ? `${l.grade_name ?? ''} — ${createdAtText(l.created_at) ?? ''}`
            : undefined,
        available: l.quantity_available,
        unit: 'kg',
        render: () => (
            <LotOptionRow
                title={l.grade_name || l.code}
                code={l.code}
                qty={`${formatQty(l.quantity_available)} kg`}
                createdLine={
                    ledger
                        ? createdAtText(l.created_at)
                        : createdAtLine(l.created_at)
                }
                ledgerStyle={ledger}
            />
        ),
    }
}
export function sortingNoteItemOption(
    it: SortingLotStockInNoteItem,
): LotOption {
    const code = it.sorting_lot_code ?? it.sorting_lot_id
    return {
        value: it.sorting_lot_id,
        label: `${code} — ${it.grade_name ?? ''}`,
        render: () => (
            <LotOptionRow
                title={it.grade_name || code}
                code={code}
                qty={`${formatQty(it.quantity)} kg`}
            />
        ),
    }
}

// ---- Lấy hương: dòng 1 = loại (không NSX/HSD) ----
export function scentingLotOption(l: ScentingLot, ledger = false): LotOption {
    return {
        value: l.id,
        // `label` GIỮ mã lô kể cả ở kiểu sổ: nó là từ khoá gõ để tìm. Chữ
        // hiện trên nút sau khi chọn thì lấy `triggerLabel` — không có mã.
        label: `${l.code} — ${l.grade_name ?? ''}`,
        // Kiểu sổ nhận lô theo ngày lấy hương của phiếu nhập, không theo ngày
        // tạo lô (M-63).
        triggerLabel: ledger
            ? `${l.grade_name ?? ''} — ${scentingDateText(l.scenting_date, l.created_at) ?? ''}`
            : undefined,
        available: l.quantity_available,
        unit: 'kg',
        render: () => (
            <LotOptionRow
                title={l.grade_name || l.code}
                code={l.code}
                qty={`${formatQty(l.quantity_available)} kg`}
                createdLine={
                    ledger
                        ? scentingDateText(l.scenting_date, l.created_at)
                        : createdAtLine(l.created_at)
                }
                ledgerStyle={ledger}
            />
        ),
    }
}
/** Lô nguồn của một dòng sổ xưởng, cho ô chọn lô nguồn trên FORM PHIẾU mở từ
 *  sổ (seed). Picker tải dần theo trang nên lô của sổ có thể chưa nằm trong
 *  trang đầu — thiếu seed thì ô hiện placeholder dù giá trị vẫn đúng. */
export function ledgerSourceNoteOption(s: {
    id: string
    code?: string
    name?: string
    available: number
    unit: string
}): LotOption {
    return {
        value: s.id,
        label: [s.code, s.name].filter(Boolean).join(' — '),
        available: s.available,
        unit: s.unit,
    }
}

/** Lô lấy hương nguồn của một mẻ sổ xưởng đang sửa (sổ đóng gói 12,5g / khác).
 *  Làm `seed` cho nguồn lô: lô đã cạn vẫn phải hiện + chọn được. */
export function scentingLedgerSeedOption(s: {
    source_scenting_lot_id: string
    source_lot_code?: string
    source_grade_name?: string
    source_scenting_date?: string | null
    source_created_at?: string
    source_available: number
}): LotOption {
    return {
        value: s.source_scenting_lot_id,
        // Mã lô ở `label` để gõ mã vẫn tìm ra; nút chỉ hiện tên và ngày
        // (xem ComboboxOption.triggerLabel).
        label: `${s.source_lot_code ?? ''} — ${s.source_grade_name ?? ''}`,
        triggerLabel: `${s.source_grade_name ?? ''} — ${
            scentingDateText(s.source_scenting_date, s.source_created_at) ?? ''
        }`,
        available: s.source_available,
        unit: 'kg',
    }
}
export function scentingNoteItemOption(
    it: ScentingLotStockInNoteItem,
): LotOption {
    const code = it.scenting_lot_code ?? it.scenting_lot_id
    return {
        value: it.scenting_lot_id,
        label: `${code} — ${it.grade_name ?? ''}`,
        render: () => (
            <LotOptionRow
                title={it.grade_name || code}
                code={code}
                qty={`${formatQty(it.quantity)} kg`}
            />
        ),
    }
}

// ---- Vật tư: dòng 1 = tên vật tư; chip vị trí; đơn vị động ----
export function packagingLotOption(l: PackagingLot): LotOption {
    const unit = l.material_unit ?? ''
    return {
        value: l.id,
        label: `${l.code} — ${l.material_name ?? ''}`,
        available: l.quantity_available,
        unit,
        render: () => (
            <LotOptionRow
                title={l.material_name || l.code}
                code={l.code}
                chips={[l.position_code]}
                qty={`${formatQty(l.quantity_available)}${unit ? ` ${unit}` : ''}`}
                createdLine={createdAtLine(l.created_at)}
            />
        ),
    }
}
export function packagingNoteItemOption(
    it: PackagingLotStockInNoteItem,
): LotOption {
    const code = it.packaging_lot_code ?? it.packaging_lot_id
    return {
        value: it.packaging_lot_id,
        label: `${code} — ${it.material_name ?? ''}`,
        render: () => (
            <LotOptionRow
                title={it.material_name || code}
                code={code}
                chips={[it.position_code]}
                qty={formatQty(it.quantity)}
            />
        ),
    }
}
